import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Avatar,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Engineering as MachinesIcon,
  MenuBook as ManualsIcon,
  Inventory as InventoryIcon,
  Build as ToolsIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchActiveNotifications } from '../store/slices/notificationsSlice';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { activeNotifications, loading: notificationsLoading } = useAppSelector((state) => state.notifications);

  useEffect(() => {
    dispatch(fetchActiveNotifications());
  }, [dispatch]);

  const quickActions = [
    {
      title: 'Maschinen verwalten',
      description: 'Maschinen anzeigen, hinzufügen und bearbeiten',
      icon: <MachinesIcon />,
      color: '#1976d2',
      path: '/machines',
    },
    {
      title: 'Anleitungen ansehen',
      description: 'Bedienungsanleitungen und Handbücher durchsuchen',
      icon: <ManualsIcon />,
      color: '#388e3c',
      path: '/manuals',
    },
    {
      title: 'Ersatzteile',
      description: 'Lagerbestand und Ersatzteile verwalten',
      icon: <InventoryIcon />,
      color: '#f57c00',
      path: '/spare-parts',
    },
    {
      title: 'Werkzeuge',
      description: 'Werkzeuge und Ausrüstung überwachen',
      icon: <ToolsIcon />,
      color: '#7b1fa2',
      path: '/tools',
    },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <Box>
      {/* Welcome Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Willkommen zurück, {user?.username}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Hier ist Ihr Dashboard für das Maschinenmatrix-System. Wählen Sie einen Bereich aus, um zu beginnen.
        </Typography>
      </Paper>

      {/* Quick Actions Grid */}
      <Grid container spacing={3}>
        {quickActions.map((action) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={action.title}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => theme.shadows[8],
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pb: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: action.color,
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {action.icon}
                </Avatar>
                <Typography variant="h6" component="h2" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'center', p: 2 }}>
                <Button
                  size="medium"
                  variant="contained"
                  onClick={() => handleNavigate(action.path)}
                  sx={{ bgcolor: action.color }}
                >
                  Öffnen
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Typography variant="h6">
                  System Status
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Alle Systeme sind betriebsbereit. Keine kritischen Probleme erkannt.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <NotificationsIcon />
                </Avatar>
                <Typography variant="h6">
                  Benachrichtigungen
                </Typography>
              </Box>
              {notificationsLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : activeNotifications.length > 0 ? (
                <List dense>
                  {activeNotifications.slice(0, 3).map((notification) => (
                    <ListItem key={notification.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={notification.message}
                        secondary={`Läuft ab: ${format(new Date(notification.expires_at), 'dd.MM.yyyy HH:mm', { locale: de })}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Keine neuen Benachrichtigungen verfügbar.
                </Typography>
              )}
              {activeNotifications.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  ... und {activeNotifications.length - 3} weitere
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity Section */}
      <Paper sx={{ mt: 3, p: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Letzte Aktivitäten
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Aktivitätsverlauf wird hier angezeigt, sobald Sie beginnen, das System zu verwenden.
        </Typography>
      </Paper>
    </Box>
  );
};

export default HomePage;